import { Store, Tag, Truck, Warehouse } from 'lucide-react'
import { useMemo } from 'react'

import {
  FilterConfig,
  FilterValues,
  GenericAdvancedFilters,
} from '@/components/newRedesign/components/filters'
import {
  useProviderCompaniesFetcher,
  useManagerCompaniesFetcher,
  useTransporterCompaniesFetcher,
  useStorageCompaniesFetcher,
} from '../hooks/useContainerQueries'

interface ContainerAdvancedFiltersProps {
  ownerCompanyId: string
  logisticCompanyId: string
  transporterCompanyId: string
  storageCompanyId: string
  category: string[]
  updateOwnerCompanyId: (_id: string) => void
  updateLogisticCompanyId: (_id: string) => void
  updateTransporterCompanyId: (_id: string) => void
  updateStorageCompanyId: (_id: string) => void
  updateCategory: (_categories: string[]) => void
  categoryOptions: { value: string; label: string }[]
}

export function ContainerAdvancedFilters({
  ownerCompanyId,
  logisticCompanyId,
  transporterCompanyId,
  storageCompanyId,
  category,
  updateOwnerCompanyId,
  updateLogisticCompanyId,
  updateTransporterCompanyId,
  updateStorageCompanyId,
  updateCategory,
  categoryOptions,
}: ContainerAdvancedFiltersProps) {
  // Usar fetchers con cache de React Query
  const searchProviderCompanies = useProviderCompaniesFetcher()
  const searchLogisticCompanies = useManagerCompaniesFetcher()
  const searchTransporterCompanies = useTransporterCompaniesFetcher()
  const searchStorageCompanies = useStorageCompaniesFetcher()

  // Filter configuration
  const filterConfigs: FilterConfig[] = useMemo(
    () => [
      {
        id: 'category',
        type: 'scrollable',
        icon: <Tag className="h-4 w-4" />,
        title: 'Categoría',
        label: 'Seleccionar...',
        options: categoryOptions,
        containerHeight: 180,
      },
      {
        id: 'ownerCompany',
        type: 'searchable',
        icon: <Store className="h-4 w-4" />,
        title: 'Compañía proveedora',
        label: 'Seleccionar...',
        fetcher: searchProviderCompanies,
        containerHeight: 200,
        searchPlaceholder: 'Buscar compañía...',
      },
      {
        id: 'logisticCompany',
        type: 'searchable',
        icon: <Truck className="h-4 w-4" />,
        title: 'Operador logístico',
        label: 'Seleccionar...',
        fetcher: searchLogisticCompanies,
        containerHeight: 200,
        searchPlaceholder: 'Buscar operador...',
      },
      {
        id: 'transporterCompany',
        type: 'searchable',
        icon: <Truck className="h-4 w-4" />,
        title: 'Transportista',
        label: 'Seleccionar...',
        fetcher: searchTransporterCompanies,
        containerHeight: 200,
        searchPlaceholder: 'Buscar transportista...',
      },
      {
        id: 'storageCompany',
        type: 'searchable',
        icon: <Warehouse className="h-4 w-4" />,
        title: 'Almacenaje',
        label: 'Seleccionar...',
        fetcher: searchStorageCompanies,
        containerHeight: 200,
        searchPlaceholder: 'Buscar almacenaje...',
      },
    ],
    [
      categoryOptions,
      searchProviderCompanies,
      searchLogisticCompanies,
      searchTransporterCompanies,
      searchStorageCompanies,
    ]
  )

  // Current filter values
  const currentValues: FilterValues = useMemo(
    () => ({
      ownerCompany:
        ownerCompanyId && ownerCompanyId !== ''
          ? parseInt(ownerCompanyId)
          : null,
      logisticCompany:
        logisticCompanyId && logisticCompanyId !== ''
          ? parseInt(logisticCompanyId)
          : null,
      transporterCompany:
        transporterCompanyId && transporterCompanyId !== ''
          ? parseInt(transporterCompanyId)
          : null,
      storageCompany:
        storageCompanyId && storageCompanyId !== ''
          ? parseInt(storageCompanyId)
          : null,
      category:
        category && category.length > 0 && !category.includes('all')
          ? category[0]
          : 'all',
    }),
    [
      ownerCompanyId,
      logisticCompanyId,
      transporterCompanyId,
      storageCompanyId,
      category,
    ]
  )

  // Handle apply filters
  const handleApply = (values: FilterValues) => {
    updateOwnerCompanyId(
      values.ownerCompany && values.ownerCompany !== 0
        ? values.ownerCompany.toString()
        : ''
    )
    updateLogisticCompanyId(
      values.logisticCompany && values.logisticCompany !== 0
        ? values.logisticCompany.toString()
        : ''
    )
    updateTransporterCompanyId(
      values.transporterCompany && values.transporterCompany !== 0
        ? values.transporterCompany.toString()
        : ''
    )
    updateStorageCompanyId(
      values.storageCompany && values.storageCompany !== 0
        ? values.storageCompany.toString()
        : ''
    )
    updateCategory(
      values.category !== 'all' ? [values.category as string] : ['all']
    )
  }

  // Handle clear filters
  const handleClear = () => {
    updateOwnerCompanyId('')
    updateLogisticCompanyId('')
    updateTransporterCompanyId('')
    updateStorageCompanyId('')
    updateCategory(['all'])
  }

  // Count active advanced filters
  const getActiveAdvancedFiltersCount = () => {
    let count = 0
    if (ownerCompanyId && ownerCompanyId !== '') count++
    if (logisticCompanyId && logisticCompanyId !== '') count++
    if (transporterCompanyId && transporterCompanyId !== '') count++
    if (storageCompanyId && storageCompanyId !== '') count++
    if (category && category.length > 0 && !category.includes('all')) count++
    return count
  }

  return (
    <GenericAdvancedFilters
      activeFiltersCount={getActiveAdvancedFiltersCount()}
      filters={filterConfigs}
      onApply={handleApply}
      onClear={handleClear}
      values={currentValues}
    />
  )
}
